import { ReactNode, useEffect, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from '../dialogs/dialog';
import { Dropdown } from 'primereact/dropdown';
import { ModuleDto } from '../../general/dtos';
import { draftRecoveryStore, ModuleRecoveryDraft } from '../../general/draft-recovery-store';
import { ModuleRecoverySession } from '../../general/use-module-draft-recovery';

export function ModuleRecoveryGate({ owner, module, children }: { owner?: string; module: ModuleDto;
    children: (value: ModuleDto, session?: ModuleRecoverySession) => ReactNode }) {
    const [loading, setLoading] = useState(!!owner);
    const [candidates, setCandidates] = useState<ModuleRecoveryDraft[]>([]);
    const [chosen, setChosen] = useState<ModuleRecoveryDraft | null>(null);
    const [ready, setReady] = useState<{ value: ModuleDto; session?: ModuleRecoverySession } | null>(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (!owner) { setReady({ value: module }); return; }
        let active = true;
        draftRecoveryStore.list(owner).then(rows => {
            if (!active) return;
            const matches = rows.filter(row => module.module_key ? row.recordId === module.module_key : row.recordId.startsWith('new:'));
            if (matches.length) { setCandidates(matches); setChosen(matches[0]); }
            else setReady({ value: module, session: { owner, recordId: module.module_key || `new:${crypto.randomUUID()}`, copyId: crypto.randomUUID(), baseUpdatedAt: module.updated_at ?? '' } });
        }).catch(() => {
            if (active) { setError('Draft recovery is unavailable in this browser.'); setReady({ value: module }); }
        }).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [owner, module]);
    const decide = async (restore: boolean) => {
        if (!chosen || !owner || busy) return;
        setBusy(true); setError('');
        try {
            if (!restore) await draftRecoveryStore.remove(chosen.id);
            setReady({ value: restore ? chosen.module : module, session: { owner,
                recordId: restore ? chosen.recordId : module.module_key || `new:${crypto.randomUUID()}`,
                copyId: restore ? chosen.copyId : crypto.randomUUID(),
                // Preserve the original version when restoring, so a later reopening still warns about a conflict.
                baseUpdatedAt: restore ? chosen.baseUpdatedAt : module.updated_at ?? '' } });
            setChosen(null);
        } catch { setError('Could not discard the recovery copy. Please try again.'); }
        finally { setBusy(false); }
    };
    return <>
        {loading ? <p role="status">Checking for a recovery draft…</p> : ready ? <>
            {error && <p role="status">{error}</p>}{children(ready.value, ready.session)}
        </> : <Dialog header="Recover interrupted draft" visible modal closable={false} closeOnEscape={false}
            style={{ width: '32rem', maxWidth: '95vw' }} onHide={() => {}}
            footer={<div className="admin-toolbar justify-content-end">
                <Button label="Discard draft" text disabled={busy} onClick={() => void decide(false)} />
                <Button label="Restore draft" icon="pi pi-history" disabled={busy} onClick={() => void decide(true)} />
            </div>}>
            <p>A recovery copy of <strong>{chosen?.module.name}</strong> was saved in this browser on {chosen && new Date(chosen.savedAt).toLocaleString()}.</p>
            {candidates.length > 1 && <><label htmlFor="recovery-choice">Draft</label><Dropdown inputId="recovery-choice"
                value={chosen?.id} options={candidates.map(row => ({ label: `${row.module.name} — ${new Date(row.savedAt).toLocaleString()}`, value: row.id }))}
                disabled={busy} onChange={event => setChosen(candidates.find(row => row.id === event.value) ?? null)} /></>}
            {!!module.module_key && chosen?.baseUpdatedAt !== (module.updated_at ?? '') && <p role="alert">This module has changed on the server since the draft began. Restoring opens the older draft for review; Save will replace the current saved module.</p>}
            <p>Restoring does not save or publish the module. Review your changes before choosing Save.</p>
            {error && <p role="alert">{error}</p>}
        </Dialog>}
    </>;
}
